// Compliance state derivation for the supplier portal. Pure JS — no
// React, no Supabase. Takes the raw rows the portal already fetches
// and turns them into the document-state / supplier-state model the
// new dashboard + /compliance page render off.
//
// Mirrors the DB trigger (compute_supplier_compliance_status) so the
// supplier portal, the internal admin views, and the compliance
// email alert all agree on what state a supplier is in. If you
// change the amber window here, change it in:
//   db/migrations/2026-05-compliance-amber-threshold-30-days.sql
// and re-run the migration.

// ============================================================================
// Configuration
// ============================================================================

// All time-based thresholds the compliance UI uses. Kept in one place so
// the threshold question ("is 30 days right?") has one answer, and so a
// future move to list_values-driven config is a single grep.
export const COMPLIANCE_THRESHOLDS = {
  // Days before expiry at which an Approved doc becomes Expiring Soon
  // and the supplier flips amber. Mirrors the DB trigger.
  EXPIRING_SOON_DAYS: 30,
};

// Document-state enum — six live states plus the special-case
// 'not_required' which Fusion sets on a per-supplier basis.
//
// 'needs_clarification' was added with the admin review flow: Fusion
// has looked at the supplier's upload and asked for more information
// before approving (e.g. "confirm the coverage amount on page 2").
// Distinct from awaiting_review (the admin hasn't actioned it yet)
// and from outstanding (Fusion rejected and the supplier must
// re-upload).
export const DOCUMENT_STATES = {
  OUTSTANDING:         'outstanding',
  AWAITING_REVIEW:     'awaiting_review',
  NEEDS_CLARIFICATION: 'needs_clarification',
  EXPIRING_SOON:       'expiring_soon',
  APPROVED:            'approved',
  EXPIRED:             'expired',
  NOT_REQUIRED:        'not_required',
};

// Supplier-state enum. 'red' takes precedence over 'amber' (one
// outstanding/expired doc blocks even if other docs are amber).
export const SUPPLIER_STATES = {
  GREEN: 'green',
  AMBER: 'amber',
  RED:   'red',
};

// Order in which doc groups render on /portal/compliance — most
// urgent first. Doc rows within each group sort by expiry ascending
// (soonest first); rows without an expiry date sort to the bottom
// of their group.
//
// Needs Clarification sits below the red states (Outstanding,
// Expired) but above the other amber state (Expiring Soon) — the
// supplier needs to act on it, but it's not blocking new work in
// the same way Outstanding/Expired are.
export const DOC_GROUP_ORDER = [
  DOCUMENT_STATES.OUTSTANDING,
  DOCUMENT_STATES.EXPIRED,
  DOCUMENT_STATES.NEEDS_CLARIFICATION,
  DOCUMENT_STATES.EXPIRING_SOON,
  DOCUMENT_STATES.AWAITING_REVIEW,
  DOCUMENT_STATES.APPROVED,
];

// ============================================================================
// Pure helpers
// ============================================================================

// ISO date string for "today" in the user's local timezone. The whole
// state machine operates on YYYY-MM-DD comparisons, never datetimes,
// because expiry_date is a DATE column with no time component.
export const todayIso = (now = new Date()) => {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// ISO date string N days from now.
export const isoDaysFromNow = (days, now = new Date()) => {
  const d = new Date(now);
  d.setDate(d.getDate() + days);
  return todayIso(d);
};

// Days between two ISO dates (positive = future, negative = past). Uses
// UTC midnight to avoid DST weirdness; both ISO inputs are date-only.
export const daysBetweenIso = (fromIso, toIso) => {
  const from = new Date(`${fromIso}T00:00:00Z`).getTime();
  const to   = new Date(`${toIso}T00:00:00Z`).getTime();
  return Math.round((to - from) / (1000 * 60 * 60 * 24));
};

// ============================================================================
// Document state derivation
// ============================================================================

// Compute the state of a single document for the supplier. Accepts the
// supplier_onboarding_documents row (or null if the supplier has no row
// for this document_key yet — happens between activation and any
// upload), and returns one of DOCUMENT_STATES.
//
// Precedence (most urgent first):
//   1. Not Required          — Fusion has marked this doc not_required for the supplier
//   2. Rejected (→Outstanding) — review_decision='rejected' means the supplier must re-upload
//   3. Expired               — approved with an expiry date in the past
//   4. Needs Clarification   — Fusion asked the supplier for more info; awaiting their reply
//   5. Awaiting Review       — file uploaded, Fusion hasn't actioned it yet
//   6. Expiring Soon         — approved with expiry within EXPIRING_SOON_DAYS
//   7. Approved              — approved with expiry > EXPIRING_SOON_DAYS away (or signed_at)
//   8. Outstanding           — no row, or row with no upload (the default for a fresh activation)
export function computeDocumentState(record, options = {}) {
  const today = options.todayIso || todayIso();
  const threshold = options.expiringSoonDays || COMPLIANCE_THRESHOLDS.EXPIRING_SOON_DAYS;
  const horizon = isoDaysFromNow(threshold, new Date(`${today}T00:00:00Z`));

  if (record?.not_required) return DOCUMENT_STATES.NOT_REQUIRED;

  // Exhaustiveness guard (compliance review-loop brief §1): the only review
  // decisions this mapper knows are null / approved / rejected /
  // needs_clarification. Anything else must NOT silently fall through to
  // awaiting_review — surface it loudly in development so a new reviewer
  // status can't go invisible on the supplier side.
  const KNOWN_DECISIONS = new Set([null, undefined, 'approved', 'rejected', 'needs_clarification']);
  if (record && !KNOWN_DECISIONS.has(record.review_decision)) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(`[compliance] Unknown review_decision "${record.review_decision}" for document_key "${record.document_key}" — the supplier state mapper does not handle it. Add it to computeDocumentState.`);
    }
  }

  // Rejected docs go back to Outstanding from the supplier's
  // perspective — they need to re-upload. The file may still exist
  // server-side for admin audit, but the state machine treats it as
  // outstanding because the supplier's action is the same.
  if (record?.review_decision === 'rejected') return DOCUMENT_STATES.OUTSTANDING;

  // Expired wins over anything else regardless of upload state — a
  // doc with a past expiry is the most urgent signal.
  if (record?.expiry_date && record.expiry_date < today) return DOCUMENT_STATES.EXPIRED;

  // Needs Clarification — Fusion has actioned the upload and asked
  // for more info. Sits above Awaiting Review because the supplier
  // now has work to do (reply or replace).
  if (record?.review_decision === 'needs_clarification') return DOCUMENT_STATES.NEEDS_CLARIFICATION;

  if (record?.supplier_uploaded_at && !record?.expiry_approved_at) return DOCUMENT_STATES.AWAITING_REVIEW;

  if (record?.expiry_approved_at && record?.expiry_date && record.expiry_date <= horizon) {
    return DOCUMENT_STATES.EXPIRING_SOON;
  }

  if (record?.expiry_approved_at || record?.signed_at) return DOCUMENT_STATES.APPROVED;

  return DOCUMENT_STATES.OUTSTANDING;
}

// A raw document_key (fusion_suppliers_h_s, payment_terms) must NEVER be shown
// to a human (document-detail-page brief §5a). When a document has no library
// label, fall back to a humanised form of the key rather than the key itself.
export function humanizeDocKey(key) {
  if (!key) return 'Untitled document';
  return String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// The label to render for a document: its library label, else a humanised key.
export function docLabelFor(label, key) {
  const l = label && String(label).trim();
  return l || humanizeDocKey(key);
}

// ============================================================================
// Supplier state derivation
// ============================================================================

// Compute the supplier's overall state from their document set.
//
// Inputs:
//   - requiredKeys: the document_keys that count for this supplier
//     (derived from risk_document_requirements for their risk tier,
//     plus any risk_override_applicable rows, minus not_required).
//   - records: their supplier_onboarding_documents rows, keyed by
//     document_key.
//
// Rules (matches the DB trigger and the admin-review brief spec):
//   RED   — any required doc is Outstanding or Expired
//   AMBER — any required doc is Expiring Soon, Needs Clarification,
//           OR Awaiting Review
//   GREEN — every required doc is Approved
//
// Awaiting Review counts as AMBER (changed 2026-08-06): a supplier is
// only green once Fusion has actually approved every required document.
// A doc that's uploaded but not yet approved holds the supplier at amber
// ("in progress") rather than green — it isn't the supplier's fault, so
// it's amber, not red. This reverses the earlier admin-review brief that
// treated Awaiting Review as green. The admin queue still surfaces these
// as the admin's call to action; they just no longer read as complete.
export function computeSupplierState(requiredKeys, records, options = {}) {
  const keys = Array.from(requiredKeys || []);
  if (keys.length === 0) return SUPPLIER_STATES.GREEN;

  const byKey = new Map();
  for (const r of records || []) {
    if (r?.document_key) byKey.set(r.document_key, r);
  }

  let anyRed = false;
  let anyAmber = false;

  for (const key of keys) {
    const record = byKey.get(key);
    const state = computeDocumentState(record, options);

    if (state === DOCUMENT_STATES.NOT_REQUIRED) continue;

    if (state === DOCUMENT_STATES.OUTSTANDING || state === DOCUMENT_STATES.EXPIRED) {
      anyRed = true;
    } else if (
      state === DOCUMENT_STATES.EXPIRING_SOON ||
      state === DOCUMENT_STATES.NEEDS_CLARIFICATION ||
      state === DOCUMENT_STATES.AWAITING_REVIEW
    ) {
      anyAmber = true;
    }
  }

  if (anyRed) return SUPPLIER_STATES.RED;
  if (anyAmber) return SUPPLIER_STATES.AMBER;
  return SUPPLIER_STATES.GREEN;
}

// ============================================================================
// Counts + grouping for the /compliance status card
// ============================================================================

// Returns counts keyed by document state. `total` excludes notRequired
// and is the denominator for the "X/Y Approved" fraction on the
// /portal/compliance status card.
export function computeComplianceCounts(requiredKeys, records, options = {}) {
  const keys = Array.from(requiredKeys || []);
  const byKey = new Map();
  for (const r of records || []) {
    if (r?.document_key) byKey.set(r.document_key, r);
  }

  const counts = {
    approved: 0,
    awaitingReview: 0,
    needsClarification: 0,
    expiringSoon: 0,
    outstanding: 0,
    expired: 0,
    notRequired: 0,
    total: 0,
  };

  for (const key of keys) {
    const record = byKey.get(key);
    const state = computeDocumentState(record, options);
    if (state === DOCUMENT_STATES.NOT_REQUIRED) {
      counts.notRequired++;
      continue;
    }
    counts.total++;
    if (state === DOCUMENT_STATES.APPROVED) counts.approved++;
    else if (state === DOCUMENT_STATES.AWAITING_REVIEW) counts.awaitingReview++;
    else if (state === DOCUMENT_STATES.NEEDS_CLARIFICATION) counts.needsClarification++;
    else if (state === DOCUMENT_STATES.EXPIRING_SOON) counts.expiringSoon++;
    else if (state === DOCUMENT_STATES.EXPIRED) counts.expired++;
    else counts.outstanding++;
  }

  return counts;
}

// The numerator for the "X/Y approved" fraction shown on the /compliance
// status card and the dashboard compliance card.
//
// NOT the same as counts.approved. The state buckets are mutually exclusive,
// so an approved document that happens to be within 30 days of expiry lands in
// expiringSoon and drops out of approved — which made a supplier with all four
// documents approved (one expiring tomorrow) read "3/4 Approved". Expiring
// Soon *is* approved; it's approved-with-a-deadline. Expired is not — it's
// past its date and no longer valid — so it stays out of the numerator.
//
// One helper, both cards: the fraction can't drift between the two.
export function approvedCurrentCount(counts) {
  return (counts?.approved || 0) + (counts?.expiringSoon || 0);
}

// Group documents by their derived state. Returns a Map keyed by state
// (in DOC_GROUP_ORDER) where each value is the array of { meta, record,
// state } entries. `meta` is the master fusion_documents entry (label,
// description, etc.); `record` is the per-supplier row (possibly null).
//
// Within each group, entries are sorted by expiry date ascending
// (soonest first); entries with no expiry_date sort last.
export function groupDocumentsByState(requiredDocs, records, options = {}) {
  const byKey = new Map();
  for (const r of records || []) {
    if (r?.document_key) byKey.set(r.document_key, r);
  }

  const groups = new Map();
  for (const state of DOC_GROUP_ORDER) groups.set(state, []);

  for (const meta of requiredDocs || []) {
    const record = byKey.get(meta.key) || null;
    const state = computeDocumentState(record, options);
    if (state === DOCUMENT_STATES.NOT_REQUIRED) continue;
    if (!groups.has(state)) groups.set(state, []);
    groups.get(state).push({ meta, record, state });
  }

  for (const [, entries] of groups) {
    entries.sort((a, b) => {
      const aExpiry = a.record?.expiry_date || '';
      const bExpiry = b.record?.expiry_date || '';
      if (!aExpiry && !bExpiry) return (a.meta.label || '').localeCompare(b.meta.label || '');
      if (!aExpiry) return 1;
      if (!bExpiry) return -1;
      return aExpiry.localeCompare(bExpiry);
    });
  }

  return groups;
}

// ============================================================================
// Stripe content — the single source of truth for what the sticky
// banner says in each state. Both the dashboard and /compliance read
// this so the stripe text is consistent across pages.
// ============================================================================

export function stripeContent(state, counts, options = {}) {
  // Manual override path — admin has flagged the supplier for non-document
  // reasons (compliance_status_override on suppliers). Doc counts no longer
  // explain the state, so we swap to a neutral "contact Fusion" message
  // instead of "X documents outstanding" (which would say 0 when docs are
  // clean). Green overrides fall through to the normal green copy — the
  // supplier doesn't need to act, so "all up to date" still reads correctly.
  if (options.overridden && state !== SUPPLIER_STATES.GREEN) {
    return {
      tone: state === SUPPLIER_STATES.RED ? 'red' : 'amber',
      message: 'Compliance status flagged by Fusion — please resolve your compliance.',
      cta: 'Manage',
    };
  }
  if (state === SUPPLIER_STATES.RED) {
    // Awaiting Review no longer counts toward Red (per the admin-
    // review brief — supplier has done their part). The red count
    // reflects what the supplier needs to fix: outstanding uploads
    // + expired approvals.
    const n = (counts?.outstanding || 0) + (counts?.expired || 0);
    return {
      tone: 'red',
      message: n === 1
        ? 'Action needed — 1 document outstanding'
        : `Action needed — ${n} documents outstanding`,
      cta: 'Resolve',
    };
  }
  if (state === SUPPLIER_STATES.AMBER) {
    // Amber now covers three cases, in priority order of what the
    // supplier can do about it:
    //   1. Needs Clarification — supplier owes a reply (interactive)
    //   2. Expiring Soon       — supplier should renew ahead of expiry
    //   3. Awaiting Review     — no supplier action; Fusion holds it
    // Lead with whichever is present, highest priority first, so the
    // stripe never tells a supplier to act on something that's actually
    // sitting with Fusion.
    const clarification = counts?.needsClarification || 0;
    const expiring = counts?.expiringSoon || 0;
    const awaiting = counts?.awaitingReview || 0;
    if (clarification > 0) {
      const n = clarification;
      return {
        tone: 'amber',
        message: n === 1
          ? '1 document needs your attention'
          : `${n} documents need your attention`,
        cta: 'Manage',
      };
    }
    if (expiring > 0) {
      return {
        tone: 'amber',
        message: expiring === 1
          ? '1 document expiring soon'
          : `${expiring} documents expiring soon`,
        cta: 'Manage',
      };
    }
    return {
      tone: 'amber',
      message: awaiting === 1
        ? '1 document with Fusion for review'
        : `${awaiting} documents with Fusion for review`,
      cta: 'Manage',
    };
  }
  return {
    tone: 'green',
    message: 'All compliance documents up to date',
    cta: 'Manage',
  };
}

// ============================================================================
// Date formatting — British format, exact day counts. Reused by the
// document row meta lines so the same dates render identically across
// /compliance and any future surface.
// ============================================================================

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// "12 Jun 2026". null/undefined → null.
export function formatDateBritish(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return `${d} ${MONTH_SHORT[m - 1]} ${y}`;
}

// "14 days" / "1 day" / "today" / "yesterday" / "3 days ago".
// Always exact — no rounding to weeks/months.
export function formatDayCount(targetIso, options = {}) {
  const today = options.todayIso || todayIso();
  const delta = daysBetweenIso(today, targetIso);
  if (delta === 0) return 'today';
  if (delta === 1) return 'tomorrow';
  if (delta === -1) return 'yesterday';
  if (delta > 0) return `${delta} days`;
  return `${Math.abs(delta)} days ago`;
}
